/** Dias corridos entre uma data "YYYY-MM-DD" e `referencia`, por contagem de
 *  dia-calendário UTC (sem conversão de fuso — evita off-by-one perto da
 *  meia-noite). `referencia` é parâmetro pra a função ficar pura e testável.
 *
 *  ⚠ Descasamento de calendário conhecido, não corrigido aqui: `iso` (o
 *  `primeiroEvento`/`primeiroDia` que os callers passam) é dia-calendário
 *  **UTC** vindo do backend (ver doc de `NumerosAuditoriaDto.PrimeiroEvento`),
 *  mas `referencia.getFullYear/Month/Date()` abaixo lêem o calendário
 *  **LOCAL** do browser (o default `referencia = new Date()` nos callers usa
 *  "agora" local). Evento mais antigo perto da meia-noite pode cair no dia
 *  UTC anterior ao dia local — off-by-one no denominador de `diasCobertos` e
 *  na data exibida no banner. Mesma imprecisão já registrada no DTO; só não
 *  estava documentada aqui, onde a próxima pessoa realmente lê. */
function diasDesde(iso: string, referencia: Date): number {
  const [y, m, d] = iso.split('-').map(Number);
  const inicio = Date.UTC(y, m - 1, d);
  const hojeUtc = Date.UTC(referencia.getFullYear(), referencia.getMonth(), referencia.getDate());
  return Math.floor((hojeUtc - inicio) / 86_400_000);
}

/**
 * Aviso 1 ("coletando desde {data}"): o gráfico da aba Uso é curto porque a
 * coleta é recente (a telemetria nasceu na Fase 2, não na instalação do
 * cliente) — não porque ninguém usa o sistema. Dispara quando o primeiro dia
 * com dado é mais novo que a janela selecionada.
 */
export function deveAvisarColetaRecente(
  primeiroDia: string | null | undefined,
  dias: number,
  referencia: Date = new Date(),
): boolean {
  if (!primeiroDia) return false;
  return diasDesde(primeiroDia, referencia) < dias;
}

/**
 * Aviso 2 ("sem dados mobile"): dispara quando a sonda de janela MÁXIMA (365d,
 * origem mobile — teto do clamp do backend, não o período selecionado pelo
 * usuário) não trouxe nenhuma linha. A pergunta é "existe QUALQUER acesso
 * mobile registrado?", não "houve mobile nos últimos N dias" — usar o período
 * selecionado geraria falso-positivo pra quem só não abriu janela estreita
 * naquele recorte, mesmo com mobile coletando há meses.
 */
export function deveAvisarSemDadosMobile(
  checagem: { telas: unknown[]; recursos: unknown[] } | undefined,
): boolean {
  return !!checagem && checagem.telas.length === 0 && checagem.recursos.length === 0;
}

/**
 * Quantos dos `dias` da janela selecionada têm como ter dado, dado que a
 * coleta só existe a partir de `primeiroDia` (retenção expurgou o resto, ou a
 * coleta/instalação é recente). Mesma conta de `deveAvisarColetaRecente` — a
 * aba Números usa isto pra não dividir "Média por dia" por dias que nunca
 * puderam ter evento (média cairia artificialmente baixa e as barras zeradas
 * dos dias sem dado se leriam como "ninguém usou" em vez de "fora do
 * histórico guardado").
 */
export function diasCobertos(
  primeiroDia: string | null | undefined,
  dias: number,
  referencia: Date = new Date(),
): number {
  if (!primeiroDia) return dias;
  const desde = diasDesde(primeiroDia, referencia);
  if (desde < 0) return dias; // primeiroDia no futuro (defensivo — não deve acontecer)
  return Math.min(dias, desde + 1);
}

/**
 * Gatilho do banner "Histórico disponível desde..." da aba Números
 * (`AbaNumeros`): `diasCobertos(...) < dias`, o MESMO corte do denominador
 * de "Média por dia" — de propósito NÃO é `deveAvisarColetaRecente` (aquele
 * usa `desde < dias`, sem o "+1 inclusive" de `diasCobertos`, e dá
 * falso-positivo quando o 1º evento cai exatamente no 1º dia da janela:
 * cobertura já é 100% mas o predicado antigo ainda dispara). Extraída como
 * função pura pra o gatilho em si ficar coberto por teste — sem isto, trocar
 * `AbaNumeros` de volta pro predicado antigo não quebraria nenhum teste (só
 * `diasCobertos` isolado era coberto, não a composição usada no componente).
 */
export function deveAvisarCoberturaInsuficiente(
  primeiroDia: string | null | undefined,
  dias: number,
  referencia: Date = new Date(),
): boolean {
  return diasCobertos(primeiroDia, dias, referencia) < dias;
}
